import { useMediaQuery, useTheme } from '@mui/material';
import React from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
  APP_SETUP_PAGE,
  MessageSettings,
  ModuleSettingsItem,
} from 'src/constants';
import { PortalConfigContext } from 'src/context';
import history from 'src/history';
import { RootState } from 'src/store';
import { saveMessageSettings } from 'src/store/settings/actions';
import { listUsers } from 'src/store/users/actions';

import { AppSettingsModalContext } from '../../Modals/AppSettingsModalContext';
import { SetupModulePageDescription } from '../AppSetupPage/SetupApp';
import SetupAppBreadCrumbs from '../AppSetupPage/SetupApp/SetupAppBreadCrumbs';
import { useEditingApp } from '../AppSetupPage/SetupApp/useEditingApp';
import { useAppSetupPaths } from '../AppSetupPage/useAppSetupPaths';
import { ModuleContainer } from '../ModuleContainer';
import { SettingsPageForm } from '../SettingsPageForm';

import { useMessagingSettingsForm } from './useMessagingSettingsForm';

const MessagingPage = () => {
  const portalConfig = React.useContext(PortalConfigContext);
  const { folderId, addAppPagePath } = useAppSetupPaths();
  const dispatch = useDispatch();
  const modalCtx = React.useContext(AppSettingsModalContext);
  const { loadedUsers, isLoading: isUsersLoading } = useSelector(
    (state: RootState) => state.users,
  );
  const messagesModule = useEditingApp();
  const messageSettings = useSelector(
    (state: RootState) => state.settings.messageSettings,
  );

  const initialValues = {
    label: messagesModule?.label || '',
    icon: messagesModule?.icon || '',
    fields: {
      messageBody:
        (messageSettings && messageSettings.fields.messageBody) ||
        `Welcome to the ${portalConfig.name} portal.`,
      isSecure: messageSettings?.fields.isSecure ?? false,
      senderUserId: messageSettings?.fields.senderUserId ?? '',
      isWelcomeMessageDisabled: Boolean(
        messageSettings?.fields?.isWelcomeMessageDisabled,
      ),
    },
  };

  type MessageSettingsUpdate = Prettify<MessageSettings & ModuleSettingsItem>;
  const onSave = async (values: MessageSettingsUpdate) => {
    const newMessageSettings = {
      ...messageSettings,
      label: values?.label,
      icon: values?.icon,
      fields: {
        ...values.fields,
      },
    };
    await dispatch(
      saveMessageSettings({
        // This typecast is unfortunate, but the intersection type was a little difficult to work with.
        messageSettings: newMessageSettings as MessageSettingsUpdate,
        currentMessagesModule: messagesModule,
        folderId,
      }),
    );

    if (!modalCtx) {
      history.push(APP_SETUP_PAGE.path);
    }
  };

  const handleCancel = () => {
    if (modalCtx) {
      modalCtx.onClose();
    } else {
      history.push(addAppPagePath);
    }
  };

  React.useEffect(() => {
    if (!loadedUsers && !isUsersLoading) {
      dispatch(listUsers(false));
    }
  }, []);

  const theme = useTheme();
  const isSm = useMediaQuery(theme.breakpoints.down('sm'));

  return loadedUsers ? (
    <ModuleContainer description={<SetupModulePageDescription />}>
      {/*
        TODO: Update this to be a strongly typed form.
        These were any typed before we introduced typesafe forms so
        this isn't a type regression, but it'd be nice to update it.
      */}
      <SettingsPageForm<any>
        BreadcrumbsComponent={<SetupAppBreadCrumbs />}
        useFormHook={useMessagingSettingsForm}
        initialFormValue={initialValues}
        handleSave={onSave}
        actionLabel={messagesModule?.disabled ? 'Enable' : 'Save'}
        shouldShowPageAction={messagesModule?.disabled}
        handleCancel={messagesModule?.disabled ? handleCancel : undefined}
        showBackButton={isSm}
        onBackButtonClick={modalCtx?.onClose ?? (() => history.goBack())}
      />
    </ModuleContainer>
  ) : null;
};

export default MessagingPage;
